// App layout: fixed sidebar + (top bar over a scrolling content area). Pages render in <Outlet/>.
import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar.jsx';
import TopBar from './TopBar.jsx';
import { pingPresence } from '../../api/presence.js';
import { pageInfo, draftSummary, onPresenceChange } from '../../lib/presence.js';
import { useAuthStore } from '../../stores/authStore.js';
import { prefetchOrderingCatalog } from '../../pages/OrderFlow.jsx';

export default function AppShell() {
  const role = useAuthStore((s) => s.user?.role);
  const [mobileNav, setMobileNav] = useState(false); // phone/tablet slide-out menu

  // Warm a client's own catalogue in the background as soon as they're in the app, so opening New
  // Order is instant. Only clients have a single implicit catalogue; reps/staff pick a client first.
  useEffect(() => {
    if (role === 'CLIENT') prefetchOrderingCatalog(null);
  }, [role]);

  // Live-presence heartbeat: while a tab is open + visible, mark this user as active every 45s so
  // the staff dashboard can show who is on the platform right now. A client's ping also says what
  // they're looking at and their order in progress, and goes out straight away when that changes
  // (page change, product opened). Best-effort.
  const location = useLocation();
  const where = useRef(location);
  where.current = location;
  const ping = useRef(() => {});
  ping.current = () => {
    if (document.visibilityState !== 'visible') return;
    const body = role === 'CLIENT' ? { page: pageInfo(where.current.pathname, where.current.search), cart: draftSummary('CLIENT') } : undefined;
    pingPresence(body).catch(() => {});
  };
  useEffect(() => {
    const beat = () => ping.current();
    onPresenceChange(beat);
    const t = setInterval(beat, 45_000);
    document.addEventListener('visibilitychange', beat);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', beat);
      onPresenceChange(null);
    };
  }, []);
  useEffect(() => {
    ping.current();
  }, [location.pathname, location.search]);

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-background">
      {/* Keyboard skip link — first focusable element, visible only when focused. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-md focus:bg-accent focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
      >
        Skip to content
      </a>
      <Sidebar mobileOpen={mobileNav} onClose={() => setMobileNav(false)} />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setMobileNav(true)} />
        <main id="main" tabIndex={-1} className="flex-1 overflow-y-auto outline-none">
          <div className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6 sm:py-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
